import React from 'react';
import { DialogueLine, EntryTake } from '../../types';

export type LineDotStatus = 'empty' | 'mine' | 'theirs' | 'multiple';

export interface LineDotsProps {
  dialogueLines: DialogueLine[];
  takes: EntryTake[];
  currentPerformer?: string;
  size?: 'sm' | 'md' | 'lg';
  interactive?: boolean;
  selectedLineId?: string | null;
  onSelectLine?: (lineId: string) => void;
  className?: string;
}

export function getLineStatus(
  lineId: string,
  takes: EntryTake[],
  currentPerformer?: string
): { status: LineDotStatus; performers: string[]; takeCount: number } {
  const lineTakes = takes.filter(t => t.lineIds && t.lineIds.includes(lineId));
  if (lineTakes.length === 0) {
    return { status: 'empty', performers: [], takeCount: 0 };
  }

  const uniquePerformers = Array.from(new Set(lineTakes.map(t => t.performerName.trim())));

  if (uniquePerformers.length > 1) {
    return { status: 'multiple', performers: uniquePerformers, takeCount: lineTakes.length };
  }

  const performer = uniquePerformers[0] || 'Anonymous';
  const isMine = currentPerformer 
    ? performer.toLowerCase() === currentPerformer.trim().toLowerCase() 
    : false;

  return {
    status: isMine ? 'mine' : 'theirs',
    performers: uniquePerformers,
    takeCount: lineTakes.length
  };
}

export const LineDots: React.FC<LineDotsProps> = ({
  dialogueLines,
  takes,
  currentPerformer,
  size = 'md',
  interactive = false,
  selectedLineId,
  onSelectLine,
  className = ''
}) => {
  if (!dialogueLines || dialogueLines.length === 0) {
    return null;
  }

  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  }[size];

  const statusStyles: Record<LineDotStatus, { bg: string; border: string; glow: string; label: string }> = {
    empty: {
      bg: 'bg-slate-700/60',
      border: 'border-slate-600',
      glow: '',
      label: 'Unrecorded'
    },
    mine: {
      bg: 'bg-emerald-500',
      border: 'border-emerald-400',
      glow: 'shadow-[0_0_8px_rgba(16,185,129,0.7)]',
      label: 'Recorded by you'
    },
    theirs: {
      bg: 'bg-amber-400',
      border: 'border-amber-300',
      glow: 'shadow-[0_0_8px_rgba(251,191,36,0.7)]',
      label: 'Recorded by friend'
    },
    multiple: {
      bg: 'bg-purple-500',
      border: 'border-purple-400',
      glow: 'shadow-[0_0_8px_rgba(168,85,247,0.7)]',
      label: 'Multiple recordings'
    }
  };

  return (
    <div className={`line-dots-group ${className}`} role="group" aria-label="Dialogue Line Completion">
      {dialogueLines.map((line, idx) => {
        const { status, performers, takeCount } = getLineStatus(line.id, takes, currentPerformer);
        const isSelected = selectedLineId === line.id;

        const tooltipText = `Line ${idx + 1} (${line.characterName || line.speaker || 'Voice'}): ` +
          (status === 'empty' 
            ? 'Not recorded' 
            : `${takeCount} take(s) by ${performers.join(', ')}`);

        return (
          <button
            key={line.id || idx}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && onSelectLine && onSelectLine(line.id)}
            title={tooltipText}
            className={`line-dot line-dot-${size} line-dot-${status} ${interactive ? 'interactive' : ''} ${isSelected ? 'selected' : ''}`}
            aria-label={tooltipText}
          />
        );
      })}
    </div>
  );
};
